import { isDesktopBreakpoint, prefersReducedMotion } from './utilities.js';

const ZOOM_SCALE = 2;
const TRANSITION_DURATION = 0.3;

/**
 * Product hover zoom component that enables zoom-on-hover for product images
 * Only works on desktop devices
 */
class ProductHoverZoom extends HTMLElement {
  #controller = new AbortController();
  #mediaContainers = [];
  #isDesktop = false;

  connectedCallback() {
    // Only initialize on desktop
    this.#isDesktop = isDesktopBreakpoint();
    if (!this.#isDesktop) return;

    // Check for reduced motion preference
    if (prefersReducedMotion()) return;

    // Listen for breakpoint changes
    const mediaQuery = matchMedia('(min-width: 750px)');
    const handleBreakpointChange = (e) => {
      this.#isDesktop = e.matches;
      if (this.#isDesktop) {
        this.#initialize();
      } else {
        this.#cleanup();
      }
    };
    mediaQuery.addEventListener('change', handleBreakpointChange, { signal: this.#controller.signal });

    // Initialize if already on desktop
    if (this.#isDesktop) {
      this.#initialize();
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#cleanup();
  }

  #initialize() {
    // Find all product media containers with images (not in zoom dialog)
    this.#mediaContainers = Array.from(
      this.querySelectorAll('.product-media-container--image:not(.dialog-zoomed-gallery .product-media-container--image)')
    );

    this.#mediaContainers.forEach((container) => {
      this.#setupHoverZoom(container);
    });
  }

  #setupHoverZoom(container) {
    const productMedia = container.querySelector('.product-media');
    const image = container.querySelector('.product-media__image');

    if (!productMedia || !image) return;

    // Ensure container has overflow hidden for zoom effect
    if (!container.style.overflow) {
      container.style.overflow = 'hidden';
    }

    // Load high-resolution image if available
    const highResUrl = image.getAttribute('data_max_resolution');
    if (highResUrl && image.src !== highResUrl) {
      const highResImage = new Image();
      highResImage.onload = () => {
        // Only replace if still in viewport and not already replaced
        if (image.getAttribute('data_max_resolution') === highResUrl) {
          image.src = highResUrl;
        }
      };
      highResImage.src = highResUrl;
    }

    const { signal } = this.#controller;

    // Mouse enter - enable zoom
    container.addEventListener(
      'mouseenter',
      () => {
        this.#handleMouseEnter(container, productMedia, image);
      },
      { signal }
    );

    // Mouse move - update zoom position
    container.addEventListener(
      'mousemove',
      (e) => {
        this.#handleMouseMove(e, container, productMedia, image);
      },
      { signal }
    );

    // Mouse leave - reset zoom
    container.addEventListener(
      'mouseleave',
      () => {
        this.#handleMouseLeave(container, productMedia, image);
      },
      { signal }
    );
  }

  #handleMouseEnter(container, productMedia, image) {
    // Add zoom class for styling
    container.classList.add('product-media-container--hover-zoom');
    productMedia.classList.add('product-media--hover-zoom');
  }

  #handleMouseMove(e, container, productMedia, image) {
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate percentage position (0-100)
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;

    // Set transform origin based on mouse position
    image.style.transformOrigin = `${xPercent}% ${yPercent}%`;

    // Apply zoom scale
    image.style.transform = `scale(${ZOOM_SCALE})`;
  }

  #handleMouseLeave(container, productMedia, image) {
    // Remove zoom class
    container.classList.remove('product-media-container--hover-zoom');
    productMedia.classList.remove('product-media--hover-zoom');

    // Reset transform
    image.style.transform = '';
    image.style.transformOrigin = '';
  }

  #cleanup() {
    this.#mediaContainers.forEach((container) => {
      const image = container.querySelector('.product-media__image');
      if (image) {
        image.style.transform = '';
        image.style.transformOrigin = '';
      }
      container.classList.remove('product-media-container--hover-zoom');
      const productMedia = container.querySelector('.product-media');
      if (productMedia) {
        productMedia.classList.remove('product-media--hover-zoom');
      }
    });
  }
}

if (!customElements.get('product-hover-zoom')) {
  customElements.define('product-hover-zoom', ProductHoverZoom);
}

